(() => {
  if (window.MairazInfiniteScroll) return;

  const ROOT_SELECTOR = '[data-infinite-scroll]';
  const ITEM_SELECTOR = '#product-grid > .grid__item';
  const STATE_KEY = 'mairazInfiniteScroll';
  const instances = new Set();

  const normalizePageUrl = (value) => {
    const url = new URL(value, window.location.origin);
    url.searchParams.delete('section_id');
    return `${url.pathname}${url.search}${url.hash}`;
  };

  const getStateKey = (sectionId) => {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    return `${url.pathname}?${url.searchParams.toString()}::${sectionId}`;
  };

  class InfiniteCollection {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.grid = root.closest('.collection')?.querySelector('#product-grid');
      this.button = root.querySelector('[data-infinite-scroll-button]');
      this.sentinel = root.querySelector('[data-infinite-scroll-sentinel]');
      this.status = root.querySelector('[data-infinite-scroll-status]');
      this.pagination = root.querySelector('[data-infinite-scroll-pagination]');
      this.nextUrl = this.readNextUrl(root);
      this.loadedUrls = new Set();
      this.seenProducts = new Set();
      this.loading = false;
      this.autoPaused = false;
      this.autoNeedsUserScroll = false;
      this.stateKey = getStateKey(this.sectionId);
      this.restoreState = this.getRestoreState();

      if (!this.grid || !this.button || !this.sentinel || !this.sectionId) return;

      this.collectSeenProducts();
      this.bindEvents();
      this.root.classList.add('is-enhanced');
      this.root.dataset.infiniteScrollReady = 'true';
      instances.add(this);

      if (this.restoreState) {
        this.restore(this.restoreState);
      } else {
        this.updateControls();
        this.observe();
        this.saveState();
      }
    }

    readNextUrl(scope) {
      const link = scope.querySelector('[data-infinite-scroll-next]');
      return link?.getAttribute('href') ? normalizePageUrl(link.getAttribute('href')) : null;
    }

    productKey(item) {
      if (item.dataset.productId) return `id:${item.dataset.productId}`;
      const productLink = item.querySelector('a[href*="/products/"]');
      if (!productLink) return null;
      return `url:${new URL(productLink.href, window.location.origin).pathname}`;
    }

    collectSeenProducts() {
      this.grid.querySelectorAll(':scope > .grid__item').forEach((item) => {
        const key = this.productKey(item);
        if (key) this.seenProducts.add(key);
      });
    }

    bindEvents() {
      this.button.addEventListener('click', () => {
        this.autoPaused = false;
        this.loadNext('button');
      });

      this.onScrollIntent = (event) => {
        if (event.type === 'keydown' && !['ArrowDown', 'End', 'PageDown', ' '].includes(event.key)) return;
        if (!this.autoNeedsUserScroll) return;
        this.autoNeedsUserScroll = false;
        this.observe();
      };
      window.addEventListener('wheel', this.onScrollIntent, { passive: true });
      window.addEventListener('touchmove', this.onScrollIntent, { passive: true });
      window.addEventListener('keydown', this.onScrollIntent);
    }

    observe() {
      this.observer?.disconnect();
      if (!this.nextUrl || this.autoPaused || this.autoNeedsUserScroll || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting) && !this.autoNeedsUserScroll) {
            this.observer.disconnect();
            this.loadNext('auto');
          }
        },
        { rootMargin: '900px 0px', threshold: 0 },
      );
      this.observer.observe(this.sentinel);
    }

    async loadNext(trigger = 'auto', options = {}) {
      if (this.loading || !this.nextUrl || !this.root.isConnected || (trigger === 'auto' && this.autoNeedsUserScroll)) {
        return;
      }

      this.loading = true;
      this.setLoading(true);
      let totalAdded = 0;

      try {
        // A page can contain only hidden or already-rendered products. Advance safely
        // through a few such pages so a valid next page is not stranded.
        for (let attempt = 0; attempt < 5 && this.nextUrl && totalAdded === 0; attempt += 1) {
          const result = await this.appendPage(this.nextUrl);
          totalAdded += result.added;
        }

        if (!options.restoring && totalAdded > 0) {
          this.announce(`${totalAdded} more product${totalAdded === 1 ? '' : 's'} loaded.`);
        }

        this.updateControls();
        this.saveState();
        if (!options.restoring) this.autoNeedsUserScroll = true;
        if (this.nextUrl && trigger !== 'restore') this.observe();
      } catch (error) {
        console.error('Mairaz infinite scroll:', error);
        this.autoPaused = true;
        this.observer?.disconnect();
        this.announce('More products could not load automatically. Select SHOW MORE to retry.');
      } finally {
        this.loading = false;
        this.setLoading(false);
      }
    }

    async appendPage(pageUrl) {
      const normalizedUrl = normalizePageUrl(pageUrl);
      if (this.loadedUrls.has(normalizedUrl)) {
        this.nextUrl = null;
        return { added: 0 };
      }

      const requestUrl = new URL(normalizedUrl, window.location.origin);
      requestUrl.searchParams.set('section_id', this.sectionId);

      const response = await fetch(requestUrl.toString(), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      const html = await response.text();
      if (!this.root.isConnected) return { added: 0 };

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incomingGrid = parsed.querySelector('#product-grid');
      const incomingRoot = parsed.querySelector(ROOT_SELECTOR);
      if (!incomingGrid) throw new Error('The next product grid was not found.');

      const fragment = document.createDocumentFragment();
      let added = 0;

      incomingGrid.querySelectorAll(':scope > .grid__item').forEach((item) => {
        const key = this.productKey(item);
        if (key && this.seenProducts.has(key)) return;

        item.querySelectorAll('img').forEach((image) => {
          image.loading = 'lazy';
          image.decoding = 'async';
          if (!image.hasAttribute('fetchpriority')) image.setAttribute('fetchpriority', 'low');
        });

        if (key) this.seenProducts.add(key);
        fragment.appendChild(item);
        added += 1;
      });

      this.grid.appendChild(fragment);
      this.loadedUrls.add(normalizedUrl);
      const candidateNext = incomingRoot ? this.readNextUrl(incomingRoot) : null;
      this.nextUrl = candidateNext && candidateNext !== normalizedUrl ? candidateNext : null;

      if (typeof window.initializeScrollAnimationTrigger === 'function') {
        window.initializeScrollAnimationTrigger();
      }

      return { added };
    }

    setLoading(isLoading) {
      this.root.classList.toggle('is-loading', isLoading);
      this.button.disabled = isLoading;
      this.button.setAttribute('aria-busy', String(isLoading));
      this.button.querySelector('[data-infinite-scroll-button-label]').textContent = isLoading ? 'LOADING…' : 'SHOW MORE';
    }

    updateControls() {
      const hasNext = Boolean(this.nextUrl);
      this.button.hidden = !hasNext;
      this.sentinel.hidden = !hasNext;
      this.root.classList.toggle('is-complete', !hasNext);
      if (!hasNext && this.loadedUrls.size > 0) this.announce('All products are shown.');
    }

    announce(message) {
      if (this.status) this.status.textContent = message;
    }

    getRestoreState() {
      const state = window.history.state?.[STATE_KEY];
      if (!state || state.key !== this.stateKey || !Array.isArray(state.loadedUrls)) return null;
      return state;
    }

    saveState(scrollY = window.scrollY) {
      if (!this.root.isConnected) return;
      const currentState = window.history.state || {};
      const searchParams = currentState.searchParams ?? window.location.search.slice(1);
      window.history.replaceState(
        {
          ...currentState,
          searchParams,
          [STATE_KEY]: {
            key: this.stateKey,
            loadedUrls: [...this.loadedUrls],
            nextUrl: this.nextUrl,
            scrollY,
          },
        },
        '',
        window.location.href,
      );
    }

    async restore(state) {
      this.observer?.disconnect();
      this.announce('Restoring products…');

      for (const url of state.loadedUrls) {
        if (!this.root.isConnected) return;
        if (this.loadedUrls.has(normalizePageUrl(url))) continue;
        this.nextUrl = normalizePageUrl(url);
        await this.loadNext('restore', { restoring: true });
      }

      if (state.nextUrl) this.nextUrl = normalizePageUrl(state.nextUrl);
      this.updateControls();
      if (this.nextUrl) this.announce('');
      this.observe();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => window.scrollTo(0, Number(state.scrollY) || 0));
      });
    }

    destroy() {
      this.observer?.disconnect();
      window.removeEventListener('wheel', this.onScrollIntent);
      window.removeEventListener('touchmove', this.onScrollIntent);
      window.removeEventListener('keydown', this.onScrollIntent);
      instances.delete(this);
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (!root.dataset.infiniteScrollReady) new InfiniteCollection(root);
    });
  };

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches(ROOT_SELECTOR)) initialize(node.parentElement || document);
        else if (node.querySelector(ROOT_SELECTOR)) initialize(node);
      });
    });

    instances.forEach((instance) => {
      if (!instance.root.isConnected) instance.destroy();
    });
  });

  const start = () => {
    initialize();
    mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
  };

  window.addEventListener('pagehide', () => {
    instances.forEach((instance) => instance.saveState(window.scrollY));
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) instances.forEach((instance) => instance.observe());
  });

  window.MairazInfiniteScroll = { initialize };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
